import CanvasPluginProvider from '../CanvasPluginProvider'
import { useIncidentStore } from '@/store/incident'
import Konva from 'konva'
import IncidentDisplay from '@/components/incidents/IncidentDisplay.vue'
import { getDeadAnimalIncidentColor } from '@/composables/incidents/incidentTimeHelper'
import { useInstanceStore } from '@/store/backendInstances.js'
import { CanvasPluginSettings } from '@/types/Canvas'
import { DeadAnimalIncident } from '@/types/Incident'
import { Reactive } from 'vue'
type ConfigType = {
  enabled: boolean
  showFalsePositive: boolean
  markerType: 'box' | 'circle' | 'dot' | 'cross'
}
class DeadAnimalIncidentPlugin extends CanvasPluginProvider<ConfigType> {
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean; incidents: DeadAnimalIncident[] }>
  constructor(settings: CanvasPluginSettings, config: ConfigType) {
    super(settings, 'deadAnimals', config, true, true)
    this.reactiveProperties = { loading: false, enabled: settings.enabled, incidents: [] }
  }

  fetchInformation(): [Promise<void>, AbortController] {
    this.reactiveProperties.incidents = []
    this.draw()
    const controller = new AbortController()
    const promise: Promise<void> = new Promise((resolve, reject) => {
      useIncidentStore()
        .loadIncidentsOpenAtSpecificTimestamp(
          'deadAnimal',
          this.settings.timestamp.toMillis(),
          [
            {
              key: 'messagePayload.falsePositive',
              operator: 'exists',
              value: this.config.showFalsePositive === true,
            },
            {
              key: 'identifier.cameraId',
              operator: 'in',
              value: Object.values(this.settings.elementPositions).map(elementPosition => elementPosition.cameraId),
            },
          ],
          undefined,
          // @ts-expect-error IncidentStore isn't typed yet
          controller.signal
        )
        .then(incidents => {
          this.reactiveProperties.incidents = incidents
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
    return [promise, controller]
  }

  draw() {
    this.group.destroyChildren()
    if (!this.reactiveProperties.incidents) return
    for (const incident of this.reactiveProperties.incidents) {
      const cameraId = incident.identifier.cameraId
      const elementPosition = this.settings.elementPositions[cameraId]
      const x = incident.messagePayload.lastDetection.x * elementPosition.width
      const y = incident.messagePayload.lastDetection.y * elementPosition.height
      const w = incident.messagePayload.lastDetection.w * elementPosition.width
      const h = incident.messagePayload.lastDetection.h * elementPosition.height
      const transform = new Konva.Transform()
      transform.translate(elementPosition.x, this.settings.elementPositions[cameraId].y)
      transform.rotate(elementPosition.rotation * (Math.PI / 180))

      const marker = DeadAnimalIncidentPlugin.buildMarker(
        {
          elementTransform: transform,
          x: x,
          y: y,
          rotation: elementPosition.rotation,
          width: w,
          height: h,
          strokeWidth: Math.max(w, h) * 15,
          stroke: getDeadAnimalIncidentColor(incident),
          attrs: { cameraId: cameraId },
        },
        this.config
      )
      incident.position = {
        transform,
        x,
        y,
        w,
        h,
        rotation: elementPosition.rotation,
        marker,
      }
      marker.on('click', ev => {
        ev.evt.preventDefault()
        this.settings.contextMenuHandler({
          x: ev.evt.clientX,
          y: ev.evt.clientY,
          component: IncidentDisplay,
          props: {
            incident: incident,
            canTriggerNewEventOverride: (props: { incident: DeadAnimalIncident }) => {
              if (
                !useInstanceStore().hasAnyPermission([
                  'deadAnimals.manage',
                  'incidents.manage',
                  'incidents.manage.advanced',
                  `incidents.${props.incident.type}.manage`,
                ])
              )
                return false
              return props.incident.state !== 'closed'
            },
          },
          on: { reload: this.forceUpdate.bind(this) },
        })
      })
      marker.on('contextmenu', ev => {
        ev.evt.preventDefault()
        this.settings.contextMenuHandler({
          x: ev.evt.clientX,
          y: ev.evt.clientY,
          component: IncidentDisplay,
          props: { incident: incident },
        })
      })
      marker.on('dblclick', ev => {
        ev.evt.preventDefault()
      })

      this.group.add(marker)
    }
  }

  doesConfigChangeNeedRerender(config: ConfigType) {
    if (config.markerType !== this.config.markerType) return true
    if (config.showFalsePositive !== this.config.showFalsePositive) return true
    return false
  }

  doesConfigChangeNeedReload(config: ConfigType) {
    if (config.showFalsePositive !== this.config.showFalsePositive) return true
    return false
  }

  static buildMarker(
    setup: {
      elementTransform: Konva.Transform
      x: number
      y: number
      rotation: number
      width: number
      height: number
      strokeWidth: number
      stroke: string
      attrs: { cameraId: string }
    },
    config: ConfigType
  ) {
    if (config.markerType === 'circle') {
      const circle = new Konva.Circle({
        x: setup.elementTransform.point({ x: setup.x, y: setup.y }).x,
        y: setup.elementTransform.point({ x: setup.x, y: setup.y }).y,
        width: setup.width,
        height: setup.height,
        strokeWidth: setup.strokeWidth,
        stroke: setup.stroke,
        strokeScaleEnabled: false,
        rotation: setup.rotation,
      })
      circle.preventDefault(false)
      return circle
    }
    if (config.markerType === 'box') {
      const rect = new Konva.Rect({
        x: setup.elementTransform.point({ x: setup.x, y: setup.y }).x,
        y: setup.elementTransform.point({ x: setup.x, y: setup.y }).y,
        width: setup.width,
        height: setup.height,
        strokeWidth: setup.strokeWidth,
        stroke: setup.stroke,
        rotation: setup.rotation,
        strokeScaleEnabled: false,
        offsetX: setup.width / 2,
        offsetY: setup.height / 2,
      })
      rect.preventDefault(false)
      return rect
    }
    if (config.markerType === 'dot') {
      const dot = new Konva.Circle({
        x: setup.elementTransform.point({ x: setup.x, y: setup.y }).x,
        y: setup.elementTransform.point({ x: setup.x, y: setup.y }).y,
        radius: 0.1,
        fillEnabled: true,
        fill: setup.stroke,
      })
      dot.preventDefault(false)
      return dot
    }

    if (config.markerType === 'cross') {
      const cross = new Konva.Line({
        position: setup.elementTransform.getTranslation(),
        points: [
          setup.x,
          setup.y,
          setup.x + setup.width,
          setup.y + setup.height,
          setup.x + setup.width / 2,
          setup.y + setup.height / 2,
          setup.x + setup.width,
          setup.y,
          setup.x,
          setup.y + setup.height,
        ],
        strokeWidth: setup.strokeWidth,
        stroke: setup.stroke,
        strokeScaleEnabled: false,
        rotation: setup.rotation,
        offsetX: setup.width / 2,
        offsetY: setup.height / 2,
      })
      cross.preventDefault(false)
      return cross
    }
    throw Error('MarkerType is unknown!')
  }
}

export default DeadAnimalIncidentPlugin
